import React from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import { gVal } from "../../../Bond Creation Historic/historicGuardianUtils";
import { getAttorneyAutocompleteNoOptionsText } from "../../../../../features/bonds/shared/utils/attorneySearch";
import {
  buildBondAttorneySearchInputHandler,
  buildBondGuardianAttorneySearchHelperText,
} from "../../../../../features/bonds/shared/utils/bondAttorneySearchWiring";

/**
 * Branching attorney UX for Bond Retrieval (VA · probate · trustee · guardianship).
 */
export default function RetrievalAttorneySection({
  index,
  guardian,
  guardians,
  data,
  updateData,
  retrievalCfg: cfg,
  keys,
  attorneys,
  loadingAttorneys,
  isDisabled,
  allCities,
  allStates,
  loadingCitiesStates,
  handleAttorneySelection,
  handleAttorneyHomeAddressChange,
  handleGoogleCallback,
  handleZipCodeChange,
  handleFieldChange,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  attorneySearchEnabled = false,
  attorneySearchInput = "",
  setAttorneySearchInput,
  debouncedSearchAttorneys,
}) {
  const mergeGuardians = (next) => updateData({ ...data, guardians: next });
  const ui = cfg.attorneyUi;
  const rootSx = ui === "probate" ? {} : { mt: 2 };
  const cardTitle = cfg.attorneyCardSubtitle || "Selected Attorney";

  const useServerSearch = attorneySearchEnabled && debouncedSearchAttorneys;

  const attorneyInputChangeHandler =
    useServerSearch && setAttorneySearchInput
      ? buildBondAttorneySearchInputHandler(
          setAttorneySearchInput,
          debouncedSearchAttorneys
        )
      : undefined;

  const attorneyDetailsBlock = (attorney) => (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: "#f9f9f9",
        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        {cardTitle}
        {String(cardTitle).endsWith(":") ? "" : ":"}
      </Typography>
      <Typography variant="body1">
        <strong>Name:</strong> {attorney.label}
      </Typography>
      <Typography variant="body1">
        <strong>Position:</strong> {attorney.position}
      </Typography>
      <Typography variant="body1">
        <strong>{ui === "va" ? "Address" : "Office Address"}:</strong>{" "}
        {attorney.address}
      </Typography>
      <Typography variant="body1">
        <strong>City/State/Zip:</strong> {attorney.city}, {attorney.state}{" "}
        {attorney.zip_code}
      </Typography>
    </Box>
  );

  const selectDisabledSpread =
    cfg.labeledSelectDisabledPropName === "isDisabled"
      ? { isDisabled }
      : { disabled: isDisabled };

  const homeAddressToggle = () =>
    ui === "probate" ? (
      <TextField
        sx={{ mt: 2, mb: 2 }}
        select
        label={
          cfg.attorneyHomeToggleLabel || "Is this Attorney's Home Address?"
        }
        value={guardian.is_attorney_home || "No"}
        onChange={(e) =>
          handleAttorneyHomeAddressChange(index, e.target.value)
        }
        fullWidth
        disabled={isDisabled}
        required
      >
        <MenuItem value="Yes">Yes</MenuItem>
        <MenuItem value="No">No</MenuItem>
      </TextField>
    ) : (
      <Box sx={{ mb: 3, mt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Step 1: Address Information
        </Typography>
        <TextField
          select
          label={cfg.attorneyHomeToggleLabel || "Use attorney's home address?"}
          value={guardian.is_attorney_home || "No"}
          onChange={(e) =>
            handleAttorneyHomeAddressChange(index, e.target.value)
          }
          fullWidth
          required
          disabled={isDisabled}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Box>
    );

  const probateHomeAddressFields = () => (
    <>
      <GoogleAutoComplete
        onAddressSelect={(address, city, state, zipCode) =>
          handleGoogleCallback(index, address, city, state, zipCode)
        }
        label={cfg.probateAttorneyHomeGoogleLabel || "Search Address"}
        value={gVal(guardian, keys, "address")}
        onChange={(e) =>
          handleFieldChange(index, keys.address, e.target.value, guardians)
        }
        required
      />
      <TextField
        sx={{ mt: 2 }}
        label="Street Address"
        value={gVal(guardian, keys, "address")}
        disabled={isDisabled}
        onChange={(e) =>
          handleFieldChange(index, keys.address, e.target.value, guardians)
        }
        fullWidth
        required
      />
      <TextField
        sx={{ mt: 2 }}
        label="ZIP Code"
        value={gVal(guardian, keys, "zip")}
        disabled={isDisabled}
        onChange={(e) =>
          handleFieldChange(index, keys.zip, e.target.value, guardians)
        }
        fullWidth
        required
      />
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>City</InputLabel>
        <Select
          value={gVal(guardian, keys, "city")}
          label="City"
          disabled={isDisabled}
          onChange={(e) =>
            handleFieldChange(index, keys.city, e.target.value, guardians)
          }
          required
        >
          {allCities.map((city) => (
            <MuiMenuItem key={city} value={city}>
              {city}
            </MuiMenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>State</InputLabel>
        <Select
          value={gVal(guardian, keys, "state")}
          label="State"
          disabled={isDisabled}
          onChange={(e) =>
            handleFieldChange(index, keys.state, e.target.value, guardians)
          }
          required
        >
          {allStates.map((stateRow) => (
            <MuiMenuItem key={stateRow} value={stateRow}>
              {stateRow}
            </MuiMenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );

  const nonProbateHomeAddressFields = () => (
    <>
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(0, 0, 0, 0.6)",
            marginBottom: "4px",
            fontWeight: 500,
          }}
        >
          Search Address
        </Typography>
        <GoogleAutoComplete
          placeholder="Search Home Address"
          callback={(address, city, state, zipCode) =>
            handleGoogleCallback(index, address, city, state, zipCode)
          }
        />
      </Box>
      <TextField
        sx={{ mt: 1, mb: 1 }}
        label="Street Address"
        value={gVal(guardian, keys, "address")}
        disabled={isDisabled}
        onChange={(e) =>
          handleFieldChange(index, keys.address, e.target.value, guardians)
        }
        fullWidth
        required
      />
      <TextField
        label="ZIP/Postal Code"
        value={gVal(guardian, keys, "zip")}
        onChange={(e) => handleZipCodeChange(e, index)}
        fullWidth
        required
        disabled={isDisabled}
      />
      {loadingCitiesStates ? (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <LabeledCreatableSelect
            label="City"
            options={allCities.map((c) => ({
              label: c,
              value: c,
            }))}
            value={
              gVal(guardian, keys, "city")
                ? {
                    label: gVal(guardian, keys, "city"),
                    value: gVal(guardian, keys, "city"),
                  }
                : null
            }
            onChange={(opt) => {
              const ug = [...guardians];
              ug[index] = {
                ...ug[index],
                [keys.city]: opt ? opt.value : "",
              };
              mergeGuardians(ug);
            }}
            placeholder="Select or add a city"
            {...selectDisabledSpread}
          />
          <LabeledCreatableSelect
            label="State"
            options={allStates.map((srow) => ({
              label: srow,
              value: srow,
            }))}
            value={
              gVal(guardian, keys, "state")
                ? {
                    label: gVal(guardian, keys, "state"),
                    value: gVal(guardian, keys, "state"),
                  }
                : null
            }
            onChange={(opt) => {
              const ug = [...guardians];
              ug[index] = {
                ...ug[index],
                [keys.state]: opt ? opt.value : "",
              };
              mergeGuardians(ug);
            }}
            placeholder="Select or add a state"
            {...selectDisabledSpread}
          />
        </>
      )}
    </>
  );

  const homeAddressSection = () => {
    if (guardian.is_attorney_home !== "Yes") return null;
    return (
      <Box
        className="grid grid-cols-1 gap-4"
        sx={{ mt: ui === "probate" ? 2 : undefined }}
      >
        {ui !== "probate" && (
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Step 2: Enter Home Address:
          </Typography>
        )}
        {ui === "probate" ? probateHomeAddressFields() : nonProbateHomeAddressFields()}
      </Box>
    );
  };

  const attorneyExtraSection = () => {
    if (ui === "va") return null;
    return (
      <>
        {homeAddressToggle()}
        {homeAddressSection()}
      </>
    );
  };

  const selectedAttorney =
    guardian.is_attorney === "Yes" && guardian.attorney
      ? attorneys.find((a) => a.value === guardian.attorney)
      : null;

  const attorneyBranch = () => {
    if (!selectedAttorney) return null;
    return (
      <>
        {attorneyDetailsBlock(selectedAttorney)}
        {attorneyExtraSection()}
      </>
    );
  };

  return (
    <Box sx={rootSx}>
      <Autocomplete
        options={attorneys}
        value={attorneys.find((a) => a.value === guardian.attorney) || null}
        getOptionLabel={(option) => option.label || ""}
        filterOptions={
          useServerSearch
            ? (options) => options
            : (options, { inputValue }) => {
                if (!inputValue || inputValue.length < 3) return [];
                const input = inputValue.toLowerCase();
                return options.filter((opt) =>
                  (opt.label || "").toLowerCase().includes(input)
                );
              }
        }
        noOptionsText={
          useServerSearch
            ? getAttorneyAutocompleteNoOptionsText(attorneySearchInput, loadingAttorneys)
            : "Type at least 3 characters to search for attorneys"
        }
        loading={loadingAttorneys}
        disabled={isDisabled}
        onInputChange={attorneyInputChangeHandler}
        renderInput={(params) => (
          <TextField
            {...params}
            label={cfg.attorneyAutocompleteLabel || "Search Attorney"}
            variant="outlined"
            fullWidth
            helperText={
              useServerSearch
                ? buildBondGuardianAttorneySearchHelperText(
                    attorneySearchInput,
                    loadingAttorneys,
                    attorneys.length,
                    ""
                  )
                : undefined
            }
            slotProps={{ input: { ...params.InputProps,
              endAdornment: (
                <>
                  {loadingAttorneys ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}}
          />
        )}
        onChange={(event, newValue) => handleAttorneySelection(index, newValue)}
      />

      {attorneyBranch()}
    </Box>
  );
}

RetrievalAttorneySection.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  data: PropTypes.shape({ guardians: PropTypes.array }).isRequired,
  updateData: PropTypes.func.isRequired,
  retrievalCfg: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingAttorneys: PropTypes.bool,
  isDisabled: PropTypes.bool,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool,
  handleAttorneySelection: PropTypes.func.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  attorneySearchEnabled: PropTypes.bool,
  attorneySearchInput: PropTypes.string,
  setAttorneySearchInput: PropTypes.func,
  debouncedSearchAttorneys: PropTypes.func,
  clearAttorneys: PropTypes.func,
};
